import { memo } from "react";
import { DragEndParams } from "react-native-draggable-flatlist";

import Header from "@/components/organisms/HeaderWatchList";

import DraggableList from "@/components/organisms/DraggableList";
import PlayerList from "@/components/organisms/PlayerList";
import { Box } from "@/components/atoms/Box";
import { Stack } from "@/components/atoms/Stack";
import { WatchlistWatchlistMetaFragment } from "@/generated/graphql";
import { PlayerCardData } from "@/types/playerCardTypes";

/**
 * Props for PlayerDetailLeftView component
 */
interface LeftColumnViewProps {
  loading: boolean;
  loadWatchList?: boolean;
  refreshing: boolean;
  players: PlayerCardData[];
  watchlists?: WatchlistWatchlistMetaFragment[];
  heightRecommendation?: number;
  onLoadMore: () => void;
  onRefresh: () => void;
  onCallbackOpen?: () => void;
  selectedWatchlist?: WatchlistWatchlistMetaFragment | null;
  onDragEnd?: (params: DragEndParams<PlayerCardData>) => void;
  onSelectWatchlist?: (value: string) => void;
  onOpenMyRoster?: () => void;
  isSmallScreen: boolean;
  /** Active search query for search-specific empty state */
  searchQuery?: string;
  /** Callback for manually adding a player when search yields no results */
  onManualAddPlayer?: () => void;
}

/**
 * View component for player detail left column with watchlist header and draggable player list
 * @param root0 - Component props
 * @param root0.players - Array of player fragments to display in list
 * @param root0.watchlists - Array of available watchlists for header dropdown
 * @param root0.selectedWatchlist - Currently selected watchlist to highlight
 * @param root0.refreshing - Whether list is being refreshed via pull-to-refresh
 * @param root0.loadWatchList - Whether watchlist data is loading
 * @param root0.heightRecommendation - Recommended height for list container padding
 * @param root0.loading - Whether player data is loading
 * @param root0.isSmallScreen - Whether rendering on mobile screen size
 * @param root0.onRefresh - Callback invoked when user pulls to refresh the list
 * @param root0.onDragEnd - Callback invoked when drag-and-drop reordering ends
 * @param root0.onLoadMore - Callback invoked when scrolling near end to load more players
 * @param root0.onCallbackOpen - Callback invoked when watchlist selection opens
 * @param root0.onSelectWatchlist - Callback invoked when watchlist is selected with watchlist ID
 * @param root0.onOpenMyRoster - Callback invoked when My Roster is selected, overrides default navigation
 * @param root0.searchQuery - Active search query for search-specific empty state
 * @param root0.onManualAddPlayer - Callback for manually adding a player when search yields no results
 */
const LeftColumnView = ({
  players,
  watchlists,
  selectedWatchlist,
  refreshing,
  loadWatchList,
  heightRecommendation,
  loading,
  isSmallScreen,
  onRefresh,
  onDragEnd,
  onLoadMore,
  onCallbackOpen,
  onSelectWatchlist,
  onOpenMyRoster,
  searchQuery,
  onManualAddPlayer,
}: LeftColumnViewProps) => (
  <Stack
    className={`w-1/4 ${isSmallScreen ? "border-r-4 border-surface-strong pr-4" : "border-r border-surface-muted/50 p-2 pt-4"}`}
  >
    <Header
      loading={loadWatchList}
      onCallbackOpen={onCallbackOpen}
      watchlists={watchlists}
      selectedWatchlist={selectedWatchlist}
      onSelectWatchlist={onSelectWatchlist}
      onOpenMyRoster={onOpenMyRoster}
      isShowPopoverFilter={loading || !!players.length}
    />
    <Stack className="flex-1">
      <Box
        testID="watchlist-scroll-area"
        // SE-5373: fill the available flex space instead of a fixed measured
        // height. The list (FlashList / FlatList) owns scroll via flex-1; a
        // fixed `screen.height - 200` overflowed shorter browser windows and
        // left players below the fold unreachable on desktop.
        className={`flex-1 ${players?.length ? "" : "justify-center"}`}
      >
        {selectedWatchlist ? (
          <DraggableList
            loadingPlayers={loading}
            heightRecommendation={heightRecommendation}
            refreshing={refreshing}
            players={players}
            onLoadMore={onLoadMore}
            onRefresh={onRefresh}
            onDragEnd={onDragEnd}
            activeWatchlist={selectedWatchlist}
          />
        ) : (
          <PlayerList
            key={players
              .slice(0, 5)
              .map(p => p.id)
              .join(",")}
            loading={loading}
            refreshing={refreshing}
            heightRecommendation={heightRecommendation}
            onLoadMore={onLoadMore}
            onRefresh={onRefresh}
            players={players}
            searchQuery={searchQuery}
            onManualAddPlayer={onManualAddPlayer}
            // SE-5038: the roster sidebar's FlashList fires onEndReached during
            // initial layout, eagerly paginating roster pages 2+ on mount
            // (sequential round-trips that blow the initial-render budget).
            // Defer pagination until the user actually scrolls; real
            // infinite-scroll is preserved on first scroll.
            deferLoadMoreUntilScroll
          />
        )}
      </Box>
    </Stack>
  </Stack>
);

LeftColumnView.displayName = "LeftColumnView";

export default memo(LeftColumnView);
